import { useEffect, useRef } from 'react';

const LINE_X0 = 60;
const LINE_LEN = 980;
const LINE_Y = 120;

// Scroll-driven timeline: the marker travels L→R and lights up each node,
// ported from the Account Editor HTML build's updateLifecycle().
export default function Lifecycle() {
  const trackRef = useRef(null);
  const progressRef = useRef(null);
  const markerRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    const progress = progressRef.current;
    const marker = markerRef.current;
    const svg = svgRef.current;
    if (!track || !progress || !marker || !svg) return;

    const nodes = svg.querySelectorAll('.lifecycle__node[data-x]');
    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = track.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      let p = scrollable > 0 ? -rect.top / scrollable : 0;
      p = Math.max(0, Math.min(1, p));

      const markerX = LINE_X0 + LINE_LEN * p;
      progress.style.strokeDashoffset = (LINE_LEN * (1 - p)).toFixed(1);
      marker.setAttribute('transform', `translate(${markerX.toFixed(1)} ${LINE_Y})`);

      nodes.forEach((n) => {
        const nx = parseFloat(n.getAttribute('data-x'));
        n.classList.toggle('is-active', markerX >= nx - 1);
      });
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section className="lifecycle" id="lifecycle">
      <div className="lifecycle__track" ref={trackRef}>
        <div className="lifecycle__sticky">
          <div className="lifecycle__content">
            <span className="section-label">The order lifecycle</span>
            <h2 className="lifecycle__title">We exist to give your customers confidence.</h2>
          </div>

          <div className="lifecycle__viz">
            <svg
              ref={svgRef}
              className="lifecycle__svg lifecycle__svg--timeline"
              viewBox="-20 30 1140 200"
              role="img"
              aria-label="Order lifecycle step-by-step timeline split into pre-purchase and post-purchase"
            >
              <g className="lifecycle__bracket lifecycle__bracket--pre">
                <path d="M60,74 L60,64 L452,64 L452,74" fill="none" stroke="#d8d4c8" strokeWidth="1.5" />
                <text x="256" y="50" textAnchor="middle">PRE-PURCHASE</text>
              </g>
              <g className="lifecycle__bracket lifecycle__bracket--post">
                <path d="M648,74 L648,64 L1040,64 L1040,74" fill="none" stroke="#1f3a5f" strokeWidth="1.5" />
                <text x="844" y="50" textAnchor="middle">POST-PURCHASE · ACCOUNT EDITOR</text>
              </g>

              <line x1="60" y1="120" x2="1040" y2="120" stroke="#e8e5db" strokeWidth="2.5" strokeLinecap="round" />
              <line
                ref={progressRef}
                className="lifecycle__progress"
                x1="60"
                y1="120"
                x2="1040"
                y2="120"
                stroke="#1f3a5f"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="980"
                strokeDashoffset="980"
              />

              <g className="lifecycle__node lifecycle__node--pre" data-x="60"><circle cx="60" cy="120" r="22" /><text x="60" y="126">1</text></g>
              <g className="lifecycle__node lifecycle__node--pre" data-x="256"><circle cx="256" cy="120" r="22" /><text x="256" y="126">2</text></g>
              <g className="lifecycle__node lifecycle__node--pre" data-x="452"><circle cx="452" cy="120" r="22" /><text x="452" y="126">3</text></g>
              <g className="lifecycle__node lifecycle__node--edit lifecycle__edit" data-x="648"><circle cx="648" cy="120" r="28" /><text x="648" y="127">4</text></g>
              <g className="lifecycle__node lifecycle__node--post" data-x="844"><circle cx="844" cy="120" r="22" /><text x="844" y="126">5</text></g>
              <g className="lifecycle__node lifecycle__node--post" data-x="1040"><circle cx="1040" cy="120" r="22" /><text x="1040" y="126">6</text></g>

              <g ref={markerRef} className="lifecycle__marker" transform="translate(60 120)">
                <circle className="lifecycle__marker-halo" r="13" />
                <circle r="7" fill="#faf4ec" stroke="#1f3a5f" strokeWidth="3" />
              </g>

              <g className="lifecycle__step lifecycle__step--pre">
                <text x="60" y="172" textAnchor="middle" className="lifecycle__step-name">Browse</text>
                <text x="60" y="191" textAnchor="middle" className="lifecycle__step-desc">Discover products</text>
                <text x="256" y="172" textAnchor="middle" className="lifecycle__step-name">Cart</text>
                <text x="256" y="191" textAnchor="middle" className="lifecycle__step-desc">Add to bag</text>
                <text x="452" y="172" textAnchor="middle" className="lifecycle__step-name">Checkout</text>
                <text x="452" y="191" textAnchor="middle" className="lifecycle__step-desc">Place the order</text>
              </g>
              <g className="lifecycle__step">
                <text x="844" y="172" textAnchor="middle" className="lifecycle__step-name">Order arrives</text>
                <text x="844" y="191" textAnchor="middle" className="lifecycle__step-desc">On its way</text>
                <text x="1040" y="172" textAnchor="middle" className="lifecycle__step-name">Returns</text>
                <text x="1040" y="191" textAnchor="middle" className="lifecycle__step-desc">Return or exchange</text>
              </g>
              <g className="lifecycle__step lifecycle__step--hl">
                <text x="648" y="180" textAnchor="middle" className="lifecycle__step-name">Edit order</text>
                <text x="648" y="199" textAnchor="middle" className="lifecycle__step-desc">Fix mistakes in your edit window</text>
              </g>
            </svg>

            {/* Vertical stepper — mobile only (the horizontal SVG doesn't fit). */}
            <ol className="lifecycle__vsteps" aria-hidden="true">
              <li className="lifecycle__vgroup">Pre-purchase</li>
              <li className="lifecycle__vstep lifecycle__vstep--pre">
                <span className="lifecycle__vnum">1</span>
                <span className="lifecycle__vbody">
                  <span className="lifecycle__vname">Browse</span>
                  <span className="lifecycle__vdesc">Discover products</span>
                </span>
              </li>
              <li className="lifecycle__vstep lifecycle__vstep--pre">
                <span className="lifecycle__vnum">2</span>
                <span className="lifecycle__vbody">
                  <span className="lifecycle__vname">Cart</span>
                  <span className="lifecycle__vdesc">Add to bag</span>
                </span>
              </li>
              <li className="lifecycle__vstep lifecycle__vstep--pre">
                <span className="lifecycle__vnum">3</span>
                <span className="lifecycle__vbody">
                  <span className="lifecycle__vname">Checkout</span>
                  <span className="lifecycle__vdesc">Place the order</span>
                </span>
              </li>
              <li className="lifecycle__vgroup lifecycle__vgroup--post">Post-purchase · Account Editor</li>
              <li className="lifecycle__vstep lifecycle__vstep--edit">
                <span className="lifecycle__vnum">4</span>
                <span className="lifecycle__vbody">
                  <span className="lifecycle__vname">Edit order</span>
                  <span className="lifecycle__vdesc">Fix mistakes in your edit window</span>
                </span>
              </li>
              <li className="lifecycle__vstep lifecycle__vstep--post">
                <span className="lifecycle__vnum">5</span>
                <span className="lifecycle__vbody">
                  <span className="lifecycle__vname">Order arrives</span>
                  <span className="lifecycle__vdesc">On its way</span>
                </span>
              </li>
              <li className="lifecycle__vstep lifecycle__vstep--post">
                <span className="lifecycle__vnum">6</span>
                <span className="lifecycle__vbody">
                  <span className="lifecycle__vname">Returns</span>
                  <span className="lifecycle__vdesc">Return or exchange</span>
                </span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
